import React, { useState, useMemo, useEffect } from 'react';
import { CompleteKundali, Language } from '../types/kundali';
import {
  Star,
  Compass,
  Calendar,
  Sparkles,
  CheckCircle,
  AlertCircle,
  Info,
  Zap,
  Eye,
  EyeOff,
  ArrowUp,
  ArrowDown
} from 'lucide-react';
import {
  getPlanetFullName,
  getZodiacName,
  getNakshatraName,
  getDignityName,
  getVaraName,
  getPlanetShortCode,
  getTithiName,
  getPanchangYogaName,
  getKaranaName,
  getAvakhadaValue
} from '../utils/language';
import { getEnhancedPlanetaryDignityList, EnhancedPlanetaryDignity } from '../utils/planetaryDignityEngine';
import {
  PopoverWrapper,
  DignityHoverCard,
  ShadbalaHoverCard,
  NatureHoverCard,
  NakshatraHoverCard
} from './PlanetaryHoverPopover';
import { PlanetaryBhaveshMatrixView } from './PlanetaryBhaveshMatrixView';

interface PlanetaryTableProps {
  kundali: CompleteKundali;
  lang?: Language;
}

export const PlanetaryTable: React.FC<PlanetaryTableProps> = ({ kundali, lang = 'en' }) => {
  const activeLang: Language = lang === 'hi' ? 'hi' : 'en';
  const isHi = activeLang === 'hi';
  const [viewMode, setViewMode] = useState<'MATRIX' | 'TABLE'>('MATRIX');
  const [stopBlink, setStopBlink] = useState(false);
  const [tooltipsEnabled, setTooltipsEnabled] = useState<boolean>(() => {
    try {
      const stored = localStorage.getItem('astro_table_hover_tooltips_enabled');
      return stored !== 'false'; // Default enabled
    } catch {
      return true;
    }
  });

  const toggleTooltips = () => {
    setTooltipsEnabled((prev) => {
      const next = !prev;
      try {
        localStorage.setItem('astro_table_hover_tooltips_enabled', String(next));
      } catch {}
      return next;
    });
  };

  const { panchang, avakhada } = kundali;

  const enhancedPlanets = useMemo(() => {
    return getEnhancedPlanetaryDignityList(kundali, activeLang);
  }, [kundali, activeLang]);

  return (
    <div className="space-y-6">
      {/* Top View Mode Navigation Banner */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 bg-astro-card border border-astro-border rounded-2xl p-3.5 shadow-2xs">
        <div className="flex items-center gap-2">
          <Compass className="w-5 h-5 text-astro-primary" />
          <div>
            <h2 className="text-sm sm:text-base font-bold text-astro-primary font-serif">
              {isHi ? 'ग्रह व भाव स्थिति केंद्र (Vedic Astrological Intelligence)' : 'Planetary & Bhavesh Intelligence Hub'}
            </h2>
            <p className="text-[11px] text-astro-secondary-text">
              {isHi ? 'ग्रहीय प्रभाव, गरिमा, षड्बल एवं १२ भावों के स्वामियों का संपूर्ण विवरण' : 'Dual-view deterministic matrix separating planetary condition from house lordship placement'}
            </p>
          </div>
        </div>

        <div className="flex items-center gap-1.5 self-start sm:self-auto bg-astro-bg p-1 rounded-xl border border-astro-border">
          <button
            type="button"
            onClick={() => setViewMode('MATRIX')}
            className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-all cursor-pointer ${
              viewMode === 'MATRIX'
                ? 'bg-astro-primary text-white shadow-xs'
                : 'text-astro-secondary-text hover:text-astro-primary'
            }`}
          >
            {isHi ? '१. ग्रहीय व भावेश मैट्रिक्स' : '1. Deterministic Matrix'}
          </button>
          <button
            type="button"
            onClick={() => setViewMode('TABLE')}
            className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-all cursor-pointer ${
              viewMode === 'TABLE'
                ? 'bg-astro-primary text-white shadow-xs'
                : 'text-astro-secondary-text hover:text-astro-primary'
            }`}
          >
            {isHi ? '२. पंचांग व शास्त्रीय तालिका' : '2. Classical Table & Panchang'}
          </button>
        </div>
      </div>

      {viewMode === 'MATRIX' && (
        <PlanetaryBhaveshMatrixView kundali={kundali} lang={activeLang} />
      )}

      {viewMode === 'TABLE' && (
        <div className="space-y-6">
          {/* Panchang & Avakhada Overview Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Panchang at Birth */}
            <div className="bg-astro-bg border border-astro-border rounded-2xl p-5 shadow-sm">
              <div className="flex items-center gap-2 mb-4 border-b border-astro-border pb-3">
                <Calendar className="w-5 h-5 text-astro-primary" />
                <h3 className="font-bold text-astro-primary font-serif text-base">
                  {lang === 'hi' ? 'जन्मकालीन पंचांग' : 'Panchang at Birth'}
                </h3>
              </div>

          <div className="grid grid-cols-2 gap-3 text-xs">
            <div className="p-3 bg-astro-card rounded-xl border border-astro-border">
              <span className="text-astro-secondary-text block text-[10px] uppercase tracking-wider font-semibold">
                {lang === 'hi' ? 'तिथि' : 'Tithi'}
              </span>
              <span className="font-semibold text-astro-text">
                {getTithiName(panchang.tithi, activeLang)}
              </span>
            </div>
            <div className="p-3 bg-astro-card rounded-xl border border-astro-border">
              <span className="text-astro-secondary-text block text-[10px] uppercase tracking-wider font-semibold">
                {lang === 'hi' ? 'नक्षत्र' : 'Nakshatra'}
              </span>
              <span className="font-semibold text-astro-primary">
                {getNakshatraName(panchang.nakshatra, activeLang)}
              </span>
            </div>
            <div className="p-3 bg-astro-card rounded-xl border border-astro-border">
              <span className="text-astro-secondary-text block text-[10px] uppercase tracking-wider font-semibold">
                {activeLang === 'hi' ? 'योग' : 'Yoga'}
              </span>
              <span className="font-semibold text-astro-text">
                {getPanchangYogaName(panchang.yoga, activeLang)}
              </span>
            </div>
            <div className="p-3 bg-astro-card rounded-xl border border-astro-border">
              <span className="text-astro-secondary-text block text-[10px] uppercase tracking-wider font-semibold">
                {activeLang === 'hi' ? 'करण' : 'Karana'}
              </span>
              <span className="font-semibold text-astro-text">
                {getKaranaName(panchang.karana, activeLang)}
              </span>
            </div>
            <div className="p-3 bg-astro-card rounded-xl border border-astro-border">
              <span className="text-astro-secondary-text block text-[10px] uppercase tracking-wider font-semibold">
                {activeLang === 'hi' ? 'वार (दिन)' : 'Vara (Day)'}
              </span>
              <span className="font-semibold text-astro-text">
                {getVaraName(panchang.vara, activeLang)}
              </span>
            </div>
            <div className="p-3 bg-astro-card rounded-xl border border-astro-border">
              <span className="text-astro-secondary-text block text-[10px] uppercase tracking-wider font-semibold">
                {lang === 'hi' ? 'लाहिड़ी अयनांश' : 'Lahiri Ayanamsha'}
              </span>
              <span className="font-semibold font-mono text-astro-primary">{panchang.ayanamshaFormatted}</span>
            </div>
          </div>
        </div>

        {/* Avakhada Chakra */}
        <div className="bg-astro-bg border border-astro-border rounded-2xl p-5 shadow-sm">
          <div className="flex items-center gap-2 mb-4 border-b border-astro-border pb-3">
            <Star className="w-5 h-5 text-astro-primary" />
            <h3 className="font-bold text-astro-primary font-serif text-base">
              {lang === 'hi' ? 'अवकहड़ा चक्र' : 'Avakhada Chakra'}
            </h3>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-3 gap-2.5 text-xs">
            <div className="p-2.5 bg-astro-card rounded-xl border border-astro-border">
              <span className="text-astro-secondary-text block text-[10px] uppercase tracking-wider font-semibold">
                {lang === 'hi' ? 'वर्ण' : 'Varna'}
              </span>
              <span className="font-semibold text-astro-text">
                {getAvakhadaValue('varna', avakhada.varna, activeLang)}
              </span>
            </div>
            <div className="p-2.5 bg-astro-card rounded-xl border border-astro-border">
              <span className="text-astro-secondary-text block text-[10px] uppercase tracking-wider font-semibold">
                {lang === 'hi' ? 'योनि' : 'Yoni'}
              </span>
              <span className="font-semibold text-astro-text">
                {getAvakhadaValue('yoni', avakhada.yoni, activeLang)}
              </span>
            </div>
            <div className="p-2.5 bg-astro-card rounded-xl border border-astro-border">
              <span className="text-astro-secondary-text block text-[10px] uppercase tracking-wider font-semibold">
                {lang === 'hi' ? 'गण' : 'Gana'}
              </span>
              <span className="font-semibold text-astro-primary">
                {getAvakhadaValue('gana', avakhada.gana, activeLang)}
              </span>
            </div>
            <div className="p-2.5 bg-astro-card rounded-xl border border-astro-border">
              <span className="text-astro-secondary-text block text-[10px] uppercase tracking-wider font-semibold">
                {lang === 'hi' ? 'नाड़ी' : 'Nadi'}
              </span>
              <span className="font-semibold text-astro-primary">
                {getAvakhadaValue('nadi', avakhada.nadi, activeLang)}
              </span>
            </div>
            <div className="p-2.5 bg-astro-card rounded-xl border border-astro-border">
              <span className="text-astro-secondary-text block text-[10px] uppercase tracking-wider font-semibold">
                {lang === 'hi' ? 'तत्त्व' : 'Tatva'}
              </span>
              <span className="font-semibold text-astro-text">
                {getAvakhadaValue('tatva', avakhada.tatva, activeLang)}
              </span>
            </div>
            <div className="p-2.5 bg-astro-card rounded-xl border border-astro-border">
              <span className="text-astro-secondary-text block text-[10px] uppercase tracking-wider font-semibold">
                {lang === 'hi' ? 'पाया' : 'Paya'}
              </span>
              <span className="font-semibold text-astro-text">
                {getAvakhadaValue('paya', avakhada.paya, activeLang)}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Planetary Details Main Table (Enhanced with Separated Dignity, Shadbala, & Interactive Hover Inspector) */}
      <div className="bg-astro-bg border border-astro-border rounded-2xl p-4 sm:p-5 shadow-sm overflow-hidden">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between mb-4 border-b border-astro-border pb-3 gap-3">
          <div className="flex items-center gap-2">
            <Compass className="w-5 h-5 text-astro-primary" />
            <div>
              <h3 className="font-bold text-astro-primary font-serif text-lg leading-tight">
                {isHi ? 'ग्रह स्थिति, गरिमा, षड्बल एवं संबंध तालिका' : 'Planetary Dignity, Strength & Relationship Table'}
              </h3>
              <p className="text-[11px] text-astro-secondary-text">
                {isHi
                  ? 'गरिमा, षड्बल, स्वभाव व नक्षत्र पर कर्सर ले जाकर विस्तृत विवरण देखें'
                  : 'Hover over Dignity, Shadbala, Nature or Nakshatra cells for detailed popover analysis'}
              </p>
            </div>
          </div>

          {/* Hover Inspector Toggle Button */}
          <button
            onClick={toggleTooltips}
            title={tooltipsEnabled ? (isHi ? 'विवरण पॉपअप बंद करें' : 'Disable Hover Popovers') : (isHi ? 'विवरण पॉपअप चालू करें' : 'Enable Hover Popovers')}
            className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold border transition-all duration-150 cursor-pointer shrink-0 ${
              tooltipsEnabled
                ? 'bg-astro-primary/15 text-astro-primary border-astro-primary/30 shadow-xs'
                : 'bg-astro-card text-astro-secondary-text border-astro-border hover:text-astro-text'
            }`}
          >
            {tooltipsEnabled ? (
              <>
                <Eye className="w-3.5 h-3.5 text-astro-primary" />
                <span>{isHi ? 'विवरण पॉपअप: चालू' : 'Inspector: ON'}</span>
              </>
            ) : (
              <>
                <EyeOff className="w-3.5 h-3.5" />
                <span>{isHi ? 'विवरण पॉपअप: बंद' : 'Inspector: OFF'}</span>
              </>
            )}
          </button>
        </div>

        <div className="w-full overflow-x-auto custom-horizontal-scrollbar">
          <table className="w-full min-w-[700px] text-left text-xs border-collapse">
            <thead>
              <tr className="border-b border-astro-border text-astro-secondary-text uppercase text-[10px] tracking-wider font-semibold bg-astro-card">
                <th className="py-3 px-3 w-[18%]">{isHi ? 'ग्रह' : 'Planet'}</th>
                <th className="py-3 px-2.5 w-[12%]">{isHi ? 'राशि व अंश' : 'Sign & Degree'}</th>
                <th className="py-3 px-2.5 w-[8%]">{isHi ? 'भाव' : 'House'}</th>
                <th className="py-3 px-2.5 w-[15%]">{isHi ? 'गरिमा (Dignity)' : 'Dignity & Status'}</th>
                <th className="py-3 px-2.5 w-[13%]">{isHi ? 'षड्बल सामर्थ्य' : 'Shadbala Strength'}</th>
                <th className="py-3 px-2.5 w-[15%]">{isHi ? 'ग्रह स्वभाव (Nature)' : 'Nature & Beneficence'}</th>
                <th className="py-3 px-2.5 w-[13%]">{isHi ? 'नक्षत्र प्रभाव' : 'Nakshatra & Impact'}</th>
                <th className="py-3 px-2.5 w-[6%]">{isHi ? 'गति' : 'Motion'}</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-astro-border text-astro-text font-medium">
              {enhancedPlanets.map((p) => {
                const rawPlanet = kundali.planets.find((pl) => pl.name === p.planet);
                const isExalted = p.isExalted;
                const isDebilitated = p.isDebilitated;
                const isOwn = p.dignity === 'Own House';
                const isMoola = p.dignity === 'Moolatrikona';
                const sb = p.shadbala;
                const sbPercent = sb ? Math.round(sb.ratio * 100) : 100;

                return (
                  <tr key={p.planet} className="hover:bg-astro-card/80 transition-colors">
                    {/* Planet Column with Direct Exalted / Debilitated / NB indicators */}
                    <td className="py-3 px-3">
                      <div className="flex items-center gap-2">
                        {/* Planet Icon / Badge with Color & Arrow */}
                        <div
                          className={`w-7 h-7 rounded-md font-bold flex items-center justify-center text-[10px] shrink-0 border relative ${
                            isExalted
                              ? 'bg-emerald-100 text-emerald-900 border-emerald-400 font-extrabold shadow-xs'
                              : isDebilitated
                              ? p.neechaBhanga.isActive
                                ? 'bg-amber-100 text-amber-900 border-amber-400 font-extrabold shadow-xs'
                                : 'bg-rose-100 text-rose-900 border-rose-400 font-extrabold shadow-xs'
                              : isOwn || isMoola
                              ? 'bg-teal-50 text-teal-800 border-teal-300'
                              : 'bg-astro-primary/10 border-astro-primary/20 text-astro-primary'
                          }`}
                          title={`${getPlanetFullName(p.planet, activeLang)} (${p.dignityLabelEn})`}
                        >
                          <span>{getPlanetShortCode(p.planet, activeLang)}</span>
                        </div>

                        <div className="flex flex-col">
                          <div className="flex items-center gap-1">
                            <span className="font-bold text-astro-text text-xs sm:text-sm">
                              {getPlanetFullName(p.planet, activeLang)}
                            </span>

                            {/* Direct Color & Arrow indicators */}
                            {isExalted && (
                              <span
                                className="inline-flex items-center text-emerald-700 dark:text-emerald-400 font-extrabold text-[11px]"
                                title={isHi ? 'उच्च ग्रह (Exalted)' : 'Exalted Planet'}
                              >
                                ↑
                              </span>
                            )}
                            {isDebilitated && !p.neechaBhanga.isActive && (
                              <span
                                className="inline-flex items-center text-rose-700 dark:text-rose-400 font-extrabold text-[11px]"
                                title={isHi ? 'नीच ग्रह (Debilitated)' : 'Debilitated Planet'}
                              >
                                ↓
                              </span>
                            )}
                            {p.neechaBhanga.isActive && (
                              <span
                                className="text-[9px] bg-amber-100 text-amber-900 border border-amber-400 font-bold px-1 rounded shadow-2xs"
                                title={isHi ? 'नीच भंग राजयोग प्राप्त' : 'Neecha Bhanga Yoga'}
                              >
                                NB
                              </span>
                            )}
                            {p.karaka?.jaiminiCode && (
                              <span
                                className="text-[8.5px] bg-purple-50 text-purple-900 border border-purple-300 font-bold px-1 rounded shadow-2xs"
                                title={`${p.karaka.jaiminiNameEn} (${p.karaka.sthiraNameEn})`}
                              >
                                {p.karaka.jaiminiCode}
                              </span>
                            )}
                            {rawPlanet?.isRetrograde && (
                              <span
                                className="text-[9px] text-astro-secondary font-bold"
                                title={isHi ? 'वक्री (Retrograde)' : 'Retrograde'}
                              >
                                (R)
                              </span>
                            )}
                          </div>

                          {/* Dasha Indicators */}
                          {(() => {
                            const md = kundali.dashas.find((d) => d.isCurrent);
                            const ad = md?.antardashas?.find((ad) => ad.isCurrent);
                            const pd = ad?.antardashas?.find((pd) => pd.isCurrent);

                            if (md?.planet === p.planet)
                              return (
                                <span
                                  onClick={() => setStopBlink(true)}
                                  className={`text-[8px] bg-astro-primary text-white px-1.5 py-0.2 rounded-full font-bold w-fit mt-0.5 cursor-pointer ${
                                    !stopBlink ? 'animate-urgent-blink' : ''
                                  }`}
                                >
                                  MD Active
                                </span>
                              );
                            if (ad?.planet === p.planet)
                              return (
                                <span className="text-[8px] bg-astro-primary/20 text-astro-primary px-1.5 py-0.2 rounded-full font-bold border border-astro-primary/30 w-fit mt-0.5">
                                  AD Active
                                </span>
                              );
                            if (pd?.planet === p.planet)
                              return (
                                <span className="text-[8px] bg-astro-primary/10 text-astro-primary px-1.5 py-0.2 rounded-full font-bold border border-astro-primary/20 w-fit mt-0.5">
                                  PD Active
                                </span>
                              );
                            return null;
                          })()}
                        </div>
                      </div>
                    </td>

                    {/* Sign & Degree */}
                    <td className="py-3 px-3">
                      <div className="font-semibold text-astro-primary">
                        {getZodiacName(p.sign, activeLang)}
                      </div>
                      <div className="font-mono text-[11px] text-astro-secondary-text">
                        {p.formattedDegree}
                      </div>
                      <span className="text-[10px] text-astro-secondary-text block">
                        {isHi ? 'स्वामी: ' : 'Lord: '}
                        {getPlanetFullName(rawPlanet?.signLord || 'Sun', activeLang)}
                      </span>
                    </td>

                    {/* House */}
                    <td className="py-3 px-3">
                      <span className="px-2 py-1 rounded-md bg-astro-card border border-astro-border text-astro-text font-bold inline-block">
                        {isHi ? `${p.house} भाव` : `H${p.house}`}
                      </span>
                    </td>

                    {/* Dignity & Status Column (With Hover Popover) */}
                    <td className="py-3 px-3 min-w-[150px]">
                      <PopoverWrapper
                        enabled={tooltipsEnabled}
                        trigger={
                          <div className="space-y-1 cursor-pointer">
                            <span
                              className={`px-2.5 py-1 rounded-full text-[11px] font-bold border inline-flex items-center gap-1 shadow-xs transition-transform hover:scale-102 ${
                                isExalted
                                  ? 'bg-emerald-100 text-emerald-900 border-emerald-400'
                                  : isDebilitated
                                  ? p.neechaBhanga.isActive
                                    ? 'bg-amber-100 text-amber-900 border-amber-400'
                                    : 'bg-rose-100 text-rose-900 border-rose-400'
                                  : isMoola
                                  ? 'bg-teal-100 text-teal-900 border-teal-400'
                                  : isOwn
                                  ? 'bg-teal-50 text-astro-primary border-teal-300'
                                  : p.dignity.includes('Friend')
                                  ? 'bg-blue-50 text-blue-800 border-blue-200'
                                  : p.dignity.includes('Enemy')
                                  ? 'bg-orange-50 text-orange-800 border-orange-200'
                                  : 'bg-astro-card text-astro-secondary-text border-astro-border'
                              }`}
                            >
                              {isExalted && '↑ '}
                              {isDebilitated && '↓ '}
                              {isHi ? p.dignityLabelHi : p.dignityLabelEn}
                            </span>

                            {p.neechaBhanga.isActive && (
                              <div className="flex items-center gap-1 text-[10px] font-bold text-amber-800 bg-amber-50 px-2 py-0.5 rounded border border-amber-300">
                                <Sparkles className="w-3 h-3 text-amber-600 shrink-0" />
                                <span>{isHi ? 'नीच भंग राजयोग' : 'Neecha Bhanga Yoga'}</span>
                              </div>
                            )}
                          </div>
                        }
                        content={<DignityHoverCard item={p} lang={activeLang} />}
                      />
                    </td>

                    {/* Separated Shadbala Strength Column (With Hover Popover) */}
                    <td className="py-3 px-3 min-w-[140px]">
                      {sb ? (
                        <PopoverWrapper
                          enabled={tooltipsEnabled}
                          trigger={
                            <div className="space-y-1 cursor-pointer">
                              <div className="flex items-center justify-between gap-1">
                                <span className="font-mono font-bold text-astro-primary text-xs">
                                  {sb.totalRupas.toFixed(2)} R
                                </span>
                                <span
                                  className={`text-[9px] font-bold px-1.5 py-0.2 rounded-full border ${
                                    sbPercent >= 120
                                      ? 'bg-emerald-50 text-emerald-800 border-emerald-300'
                                      : sbPercent >= 100
                                      ? 'bg-teal-50 text-teal-800 border-teal-300'
                                      : sbPercent >= 80
                                      ? 'bg-amber-50 text-amber-800 border-amber-300'
                                      : 'bg-rose-50 text-rose-800 border-rose-300'
                                  }`}
                                >
                                  {sbPercent}%
                                </span>
                              </div>
                              <div className="h-1.5 w-full bg-astro-card border border-astro-border rounded-full overflow-hidden">
                                <div
                                  className={`h-full rounded-full ${
                                    sbPercent >= 100 ? 'bg-emerald-500' : sbPercent >= 80 ? 'bg-amber-500' : 'bg-rose-500'
                                  }`}
                                  style={{ width: `${Math.min(100, (sbPercent / 150) * 100)}%` }}
                                />
                              </div>
                              <span className="text-[9px] text-astro-secondary-text block">
                                {isHi ? 'अपेक्षित: ' : 'Req: '}{sb.requiredRupas.toFixed(1)} R
                              </span>
                            </div>
                          }
                          content={<ShadbalaHoverCard item={p} lang={activeLang} />}
                        />
                      ) : (
                        <span className="text-[11px] text-astro-secondary-text italic">
                          {isHi ? 'छाया ग्रह' : 'Shadow Planet'}
                        </span>
                      )}
                    </td>

                    {/* Nature & Functional Role Column (With Hover Popover) */}
                    <td className="py-3 px-3 min-w-[160px]">
                      <PopoverWrapper
                        enabled={tooltipsEnabled}
                        trigger={
                          <div className="space-y-1 cursor-pointer">
                            <div className="flex items-center gap-1.5 flex-wrap">
                              {p.isYogakaraka ? (
                                <span className="px-2 py-0.5 rounded-md bg-amber-100 text-amber-900 border border-amber-400 text-[10px] font-bold inline-flex items-center gap-1 shadow-xs">
                                  <Star className="w-3 h-3 fill-amber-500 text-amber-600" />
                                  {isHi ? 'योगकारक' : 'Yogakaraka'}
                                </span>
                              ) : p.functionalNature === 'Functional Benefic' ? (
                                <span className="px-2 py-0.5 rounded-md bg-emerald-100 text-emerald-900 border border-emerald-300 text-[10px] font-bold">
                                  {isHi ? 'कार्यात्मक शुभ' : 'Functional Benefic'}
                                </span>
                              ) : p.functionalNature === 'Functional Malefic' ? (
                                <span className="px-2 py-0.5 rounded-md bg-rose-100 text-rose-900 border border-rose-300 text-[10px] font-bold">
                                  {isHi ? 'कार्यात्मक अशुभ' : 'Functional Malefic'}
                                </span>
                              ) : (
                                <span className="px-2 py-0.5 rounded-md bg-astro-card text-astro-secondary-text border border-astro-border text-[10px] font-semibold">
                                  {isHi ? 'तटस्थ' : 'Neutral'}
                                </span>
                              )}

                              <span className="text-[10px] text-astro-secondary-text">
                                ({isHi ? p.naturalNatureHi : p.naturalNature})
                              </span>
                            </div>

                            <span className="text-[10px] text-astro-secondary-text block leading-tight truncate max-w-[170px]" title={p.natureRoleDescription}>
                              {p.natureRoleDescription}
                            </span>
                          </div>
                        }
                        content={<NatureHoverCard item={p} lang={activeLang} />}
                      />
                    </td>

                    {/* Nakshatra & Cosmic Impact Column (With Hover Popover) */}
                    <td className="py-3 px-3 min-w-[190px]">
                      <PopoverWrapper
                        enabled={tooltipsEnabled}
                        trigger={
                          <div className="space-y-1 cursor-pointer">
                            <div className="flex items-center justify-between gap-1">
                              <span className="font-bold text-astro-text text-xs">
                                {getNakshatraName(p.nakshatraImpact.nakshatra, activeLang)}
                              </span>
                              <span className="font-mono text-astro-primary text-[10px] font-semibold">
                                {isHi ? `पद ${p.nakshatraImpact.pada}` : `Pada ${p.nakshatraImpact.pada}`}
                              </span>
                            </div>

                            <div className="flex items-center gap-1.5 flex-wrap text-[10px]">
                              <span className="text-astro-secondary-text">
                                {isHi ? 'स्वामी: ' : 'Lord: '}
                                {getPlanetFullName(p.nakshatraImpact.lord, activeLang)}
                              </span>
                              <span>•</span>
                              <span className="text-astro-primary font-medium truncate max-w-[110px]" title={isHi ? p.nakshatraImpact.deityHi : p.nakshatraImpact.deity}>
                                {isHi ? p.nakshatraImpact.deityHi : p.nakshatraImpact.deity}
                              </span>
                            </div>

                            {/* Navtara Impact Indicator */}
                            <div className="flex items-center gap-1.5 pt-0.5">
                              <span
                                className={`px-2 py-0.5 rounded text-[9px] font-bold inline-flex items-center gap-0.5 ${
                                  p.nakshatraImpact.isTarabalaFavorable
                                    ? 'bg-emerald-50 text-emerald-800 border border-emerald-300'
                                    : 'bg-amber-50 text-amber-800 border border-amber-300'
                                }`}
                              >
                                {p.nakshatraImpact.isTarabalaFavorable ? (
                                  <CheckCircle className="w-2.5 h-2.5 text-emerald-600" />
                                ) : (
                                  <AlertCircle className="w-2.5 h-2.5 text-amber-600" />
                                )}
                                {isHi ? p.nakshatraImpact.navtaraTypeHi.split(' ')[0] : p.nakshatraImpact.navtaraType.split(' ')[0]}
                              </span>
                              <span
                                className="text-[10px] text-astro-secondary-text truncate max-w-[130px]"
                                title={isHi ? p.nakshatraImpact.impactDescriptionHi : p.nakshatraImpact.impactDescriptionEn}
                              >
                                {isHi ? p.nakshatraImpact.impactDescriptionHi : p.nakshatraImpact.impactDescriptionEn}
                              </span>
                            </div>
                          </div>
                        }
                        content={<NakshatraHoverCard item={p} lang={activeLang} />}
                      />
                    </td>

                    {/* Motion */}
                    <td className="py-3 px-3">
                      {rawPlanet?.isRetrograde ? (
                        <span className="px-2 py-0.5 rounded-full bg-astro-secondary/15 text-astro-secondary border border-[#E2DED5] text-[10px] font-bold inline-flex items-center gap-1">
                          <span className="w-1.5 h-1.5 rounded-full bg-astro-secondary" />
                          {isHi ? 'वक्री (R)' : 'Retrograde (R)'}
                        </span>
                      ) : (
                        <span className="text-astro-secondary-text">{isHi ? 'मार्गी' : 'Direct'}</span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  )}
</div>
);
};

